import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { toPng, toJpeg } from "html-to-image";
import jsPDF from "jspdf";
import { ArrowLeft, Download, FilePdf, UploadSimple, X, FloppyDisk } from "@phosphor-icons/react";
import { TEMPLATES, FORMATS } from "../components/posters/Templates";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLOR_PRESETS = ["#FF7A59", "#34C759", "#FFCC00", "#0EA5E9", "#8B5CF6", "#EC4899", "#1A1A1A"];
const DEFAULT_HEADLINE = "Share your feedback & get a chance to win a reward";

function deriveRewardHighlight(segments = []) {
  let maxPct = 0;
  let freeItem = null;
  segments.forEach((s) => {
    if (s.type === "discount") {
      const n = parseFloat(s.value) || 0;
      if (n > maxPct) maxPct = n;
    } else if (s.type === "free_item" && !freeItem && (s.value || s.label)) {
      freeItem = s.value || s.label;
    }
  });
  if (maxPct > 0) return `Win up to ${maxPct}% OFF`;
  if (freeItem) return `Win a ${freeItem}`;
  return "Win a reward";
}

export default function OutletPoster() {
  const { id } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [format, setFormat] = useState("a4"); // a4 | poster | square
  const [template, setTemplate] = useState("minimal");
  const [primaryColor, setPrimaryColor] = useState("#FF7A59");
  const [headline, setHeadline] = useState(DEFAULT_HEADLINE);
  const [subtext, setSubtext] = useState("");
  const [rewardHighlight, setRewardHighlight] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const fileRef = useRef(null);
  const posterRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/outlets/${id}`);
        setOutlet(data);
        setBusinessName(data.name || "");
        setLogoUrl(data.logo_url || null);
        const p = data.poster_settings || {};
        setFormat(p.format || "a4");
        setTemplate(p.template || "minimal");
        setPrimaryColor(p.primary_color || "#FF7A59");
        setHeadline(p.headline || DEFAULT_HEADLINE);
        setSubtext(p.subtext || "");
        setRewardHighlight(p.reward_highlight || deriveRewardHighlight(data.segments));
      } catch {
        toast.error("Failed to load outlet");
      } finally { setLoading(false); }
    })();
  }, [id]);

  const publicUrl = outlet ? `${window.location.origin}/r/${outlet.slug}` : "";
  const logoFullUrl = logoUrl
    ? (logoUrl.startsWith("http") ? logoUrl : `${BACKEND_URL || ""}${logoUrl}`)
    : null;

  const cfg = useMemo(() => ({
    businessName,
    logoUrl: logoFullUrl,
    primaryColor,
    headline,
    subtext,
    rewardHighlight,
    publicUrl,
  }), [businessName, logoFullUrl, primaryColor, headline, subtext, rewardHighlight, publicUrl]);

  const TemplateComp = TEMPLATES.find((t) => t.id === template)?.Component || TEMPLATES[0].Component;
  const fmt = FORMATS.find((f) => f.id === format) || FORMATS[0];
  const size = fmt.size;

  const pickLogo = () => fileRef.current?.click();

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please select an image");
    if (file.size > 4 * 1024 * 1024) return toast.error("Logo must be smaller than 4MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/outlets/${id}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLogoUrl(data.url);
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    setLogoUrl(null);
    try { await api.put(`/outlets/${id}`, { logo_url: "" }); } catch {}
  };

  const savePreset = async () => {
    setSaving(true);
    try {
      await api.put(`/outlets/${id}`, {
        poster_settings: {
          format,
          template,
          primary_color: primaryColor,
          headline,
          subtext,
          reward_highlight: rewardHighlight,
        },
      });
      toast.success("Saved");
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  // Capture the rendered poster DOM at its exact pixel size.
  // html-to-image uses SVG foreignObject → browser-identical font rendering.
  const renderDataUrl = async (mode = "png") => {
    const node = posterRef.current;
    if (!node) throw new Error("Preview not ready");

    // Ensure all fonts are loaded before capture
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch {}
    }

    const opts = {
      width: size.w,
      height: size.h,
      canvasWidth: size.w * 2, // 2x for high-DPI
      canvasHeight: size.h * 2,
      pixelRatio: 2,
      cacheBust: true,
      // Note: backgroundColor removed - let template CSS control it
      style: {
        margin: "0",
        // override any inherited scaling
        transform: "none",
        transformOrigin: "0 0",
      },
    };

    if (mode === "jpeg") return await toJpeg(node, { ...opts, quality: 0.95 });
    return await toPng(node, opts);
  };

  const filename = (ext) => `${(outlet?.slug || "poster")}-${template}-${format}.${ext}`;

  const downloadPNG = async () => {
    setDownloading(true);
    try {
      const dataUrl = await renderDataUrl("png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename("png");
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("PNG downloaded");
    } catch (e) {
      console.error(e);
      toast.error("Download failed");
    } finally { setDownloading(false); }
  };

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const dataUrl = await renderDataUrl("jpeg");
      // Page size in mm matches the format
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: [fmt.pdf.w, fmt.pdf.h],
      });
      pdf.addImage(dataUrl, "JPEG", 0, 0, fmt.pdf.w, fmt.pdf.h, undefined, "FAST");
      pdf.save(filename("pdf"));
    } catch (e) {
      console.error(e);
      toast.error("PDF download failed");
    } finally { setDownloading(false); }
  };

  if (loading) return <DashboardLayout><div className="text-[var(--ink-muted)]">Loading…</div></DashboardLayout>;
  if (!outlet) return null;

  // Preview scale to fit available area (right column ~520px wide)
  const maxPreviewW = 520;
  const previewScale = Math.min(1, maxPreviewW / size.w);

  return (
    <DashboardLayout
      title={`Poster — ${outlet.name}`}
      actions={
        <>
          <Link to={`/outlet/${id}`} className="btn-ghost" data-testid="poster-back-btn" style={{ padding: "8px 14px", fontSize: 13 }}>
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
          <button onClick={savePreset} disabled={saving} data-testid="poster-save-btn" className="btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }}>
            <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save preset"}
          </button>
        </>
      }
    >
      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT: controls */}
        <div className="lg:col-span-2 space-y-5">
          {/* Format selector */}
          <div className="card-pop p-5">
            <h3 className="display text-lg font-extrabold">Format</h3>
            <p className="text-xs text-[var(--ink-muted)] mt-0.5">Choose the physical size you'll print at.</p>
            <div className="mt-3 grid grid-cols-1 gap-3">
              {FORMATS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFormat(f.id)}
                  data-testid={`poster-format-${f.id}`}
                  className={`text-left rounded-xl border-[1.5px] p-3 transition-all ${
                    format === f.id ? "border-[var(--ink)] bg-[var(--pastel-yellow)] shadow-[3px_3px_0_0_#1A1A1A]" : "border-[var(--border-soft)] hover:border-[var(--ink)]"
                  }`}
                >
                  <div className="font-bold text-sm">{f.name}</div>
                  <div className="text-[11px] text-[var(--ink-muted)] mt-1">{f.size.w} × {f.size.h} px</div>
                </button>
              ))}
            </div>
          </div>

          {/* Template picker */}
          <div className="card-pop p-5">
            <h3 className="display text-lg font-extrabold">Template</h3>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTemplate(t.id)}
                  data-testid={`poster-template-${t.id}`}
                  className={`text-left rounded-xl border-[1.5px] p-3 transition-all ${
                    template === t.id ? "border-[var(--ink)] bg-[var(--secondary)] shadow-[3px_3px_0_0_#1A1A1A]" : "border-[var(--border-soft)] hover:border-[var(--ink)]"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${template === t.id ? "bg-[var(--primary)]" : "bg-[var(--border-soft)]"}`} />
                    <div className="font-bold text-sm">{t.name}</div>
                  </div>
                  <div className="text-[11px] text-[var(--ink-muted)] mt-1">{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Customization */}
          <div className="card-pop p-5 space-y-4">
            <h3 className="display text-lg font-extrabold">Customize</h3>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Business name</label>
              <input data-testid="poster-business-input" className="input-pop mt-1.5" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Logo</label>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogoChange} data-testid="poster-logo-input" />
              {logoUrl ? (
                <div className="mt-2 flex items-center gap-3 rounded-xl border-[1.5px] border-[var(--ink)] p-2 bg-white">
                  <img src={logoFullUrl} alt="logo" className="h-14 w-14 object-contain rounded" />
                  <button onClick={pickLogo} disabled={uploading} data-testid="poster-logo-change-btn" className="text-xs font-bold underline">
                    {uploading ? "Uploading…" : "Change"}
                  </button>
                  <button onClick={removeLogo} data-testid="poster-logo-remove-btn" className="ml-auto h-8 w-8 rounded-lg border border-[var(--ink)] flex items-center justify-center">
                    <X size={14} weight="bold" />
                  </button>
                </div>
              ) : (
                <button onClick={pickLogo} disabled={uploading} data-testid="poster-logo-pick-btn" className="mt-2 w-full rounded-xl border-[1.5px] border-dashed border-[var(--ink)] py-5 text-sm font-bold inline-flex items-center justify-center gap-2 hover:bg-[var(--secondary)]">
                  <UploadSimple size={16} weight="bold" /> {uploading ? "Uploading…" : "Upload logo (PNG/SVG)"}
                </button>
              )}
              <p className="text-[11px] text-[var(--ink-muted)] mt-1">Optional · max 4MB · transparent PNG looks best</p>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Primary color</label>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {COLOR_PRESETS.map((c) => (
                  <button key={c} onClick={() => setPrimaryColor(c)} data-testid={`poster-color-${c.replace("#", "")}`}
                    aria-label={c}
                    className="h-8 w-8 rounded-lg border-[1.5px] border-[var(--ink)]"
                    style={{ background: c, outline: primaryColor === c ? "2px solid #1A1A1A" : "none", outlineOffset: 2 }}
                  />
                ))}
                <input type="color" value={primaryColor} onChange={(e) => setPrimaryColor(e.target.value)} data-testid="poster-color-custom"
                  className="h-8 w-12 rounded-lg border-[1.5px] border-[var(--ink)] cursor-pointer" />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Headline</label>
              <textarea data-testid="poster-headline-input" rows={2} className="input-pop mt-1.5" value={headline} onChange={(e) => setHeadline(e.target.value)} maxLength={120} />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Subtext (optional)</label>
              <input data-testid="poster-subtext-input" className="input-pop mt-1.5" value={subtext} onChange={(e) => setSubtext(e.target.value)} maxLength={120} placeholder="Short extra line" />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Reward highlight</label>
              <input data-testid="poster-reward-input" className="input-pop mt-1.5" value={rewardHighlight} onChange={(e) => setRewardHighlight(e.target.value)} maxLength={40} placeholder="e.g., Win up to 20% OFF" />
            </div>
          </div>

          {/* Downloads */}
          <div className="card-pop p-5">
            <h3 className="display text-lg font-extrabold">Download</h3>
            <p className="text-xs text-[var(--ink-muted)] mt-0.5">{fmt.name} · {size.w}×{size.h}px capture (2× DPI)</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <button onClick={downloadPNG} disabled={downloading} data-testid="poster-download-png-btn" className="btn-primary">
                <Download size={16} weight="bold" /> {downloading ? "Rendering…" : "Download PNG"}
              </button>
              <button onClick={downloadPDF} disabled={downloading} data-testid="poster-download-pdf-btn" className="btn-ghost">
                <FilePdf size={16} weight="bold" /> {downloading ? "Rendering…" : "Download PDF"}
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT: live preview — rendered at REAL size, scaled visually only */}
        <div className="lg:col-span-3">
          <div className="card-pop p-5 sticky top-24">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Live preview</div>
                <div className="text-sm text-[var(--ink)] font-semibold">
                  {TEMPLATES.find((t) => t.id === template)?.name} · {fmt.name}
                </div>
              </div>
            </div>

            <div className="rounded-xl bg-[var(--bg)] border border-[var(--border-soft)] p-4 flex justify-center" data-testid="poster-preview-wrap">
              <div style={{ width: size.w * previewScale, height: size.h * previewScale, position: "relative" }}>
                {/* The actual exported node lives at full size and is captured 1:1 */}
                <div
                  style={{
                    transform: `scale(${previewScale})`,
                    transformOrigin: "top left",
                    width: size.w,
                    height: size.h,
                    position: "absolute",
                    top: 0,
                    left: 0,
                  }}
                >
                  <TemplateComp cfg={cfg} format={format} ref={posterRef} />
                </div>
              </div>
            </div>
            <div className="mt-3 text-[11px] text-[var(--ink-muted)] text-center">
              QR links to <span className="font-bold">{publicUrl}</span> — preview matches export pixel-for-pixel.
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}